import { computed, ComputedRef, isRef, Ref, ref } from 'vue'
import axios from 'axios'
import { isNullish } from '@tinyurldev/tu-ui-vue'
import type { Organization } from '../Data/Models/OrganizationModel.ts'
import type { Team } from '../Data/Models/TeamModel.ts'
import StatsTimelineAPI from '@Modules/analytics/Api/StatsTimelineApi.ts'
import QuickStatsAPI from '@Modules/analytics/Api/QuickStatsApi.ts'
import HourlyPopularityStatsAPI from '@Modules/analytics/Api/HourlyPopularityStatsApi.ts'
import DailyPopularityStatsAPI from '@Modules/analytics/Api/DailyPopularityStatsApi.ts'
import TopReferrerStatsAPI from '@Modules/analytics/Api/TopReferrerStatsApi.ts'
import TopLanguageStatsAPI from '@Modules/analytics/Api/TopLanguageStatsApi.ts'
import WorldTrafficAPI from '@Modules/analytics/Api/WorldTrafficApi.ts'
import GeneralStatsAPI from '@Modules/analytics/Api/GeneralStatsApi.ts'
import TimelineItemCollection from '@Modules/analytics/DTOs/TimelineItemCollection.ts'
import ClickStat from '@Modules/analytics/DTOs/ClickStat.ts'
import HourlyPopularityStats from '@Modules/analytics/DTOs/HourlyPopularityStats.ts'
import DailyPopularityStats from '@Modules/analytics/DTOs/DailyPopularityStats.ts'
import TopReferrerStats from '@Modules/analytics/DTOs/TopReferrerStats.ts'
import TopLanguageStats from '@Modules/analytics/DTOs/TopLanguagesStats.ts'
import WorldTrafficStats from '@Modules/analytics/DTOs/WorldTrafficStats.ts'
import { ValidWorldMapTypes } from '@Modules/analytics/Views/WorldTrafficChart/WorldTraffic'
import GeneralStats from '@Modules/analytics/DTOs/GeneralStats.ts'
import TimePeriods, { Period } from '@Modules/analytics/Helpers/TimePeriods.ts'
import { logError } from '@Resources/js/helpers/logger.js'

type apis = {
    timeline: typeof StatsTimelineAPI,
    quickStats: typeof QuickStatsAPI,
    popularTimes: typeof HourlyPopularityStatsAPI,
    popularDays: typeof DailyPopularityStatsAPI,
    topReferrer: typeof TopReferrerStatsAPI,
    topLanguages: typeof TopLanguageStatsAPI,
    worldTraffic: typeof WorldTrafficAPI,
    generalStats: typeof GeneralStatsAPI,
};

export type AnalyticsViewModelOptions = {
  org: Ref<Organization | null>;
  team: Ref<Team | null>;
  alias: Ref<string | null>;
  tag?: Ref<string | null>;
  isDebugMode?: boolean;
  debugDataType?: string | null;
}

export interface iAnalyticsViewModel {
  setRouter: (
    key: keyof apis,
    router: Pick<HttpInterface, 'get'>
  ) => iAnalyticsViewModel
  fetch: (period?: Period, archived?: boolean) => Promise<void>
  fetchWorldTrafficMap: (map: ValidWorldMapTypes) => Promise<void>
  download: (options?: { period?: Period; memberEmail?: string | null; archived?: boolean }) => Promise<void>
  loading: ComputedRef<boolean>
  error: ComputedRef<string | null>
  currentPeriod: ComputedRef<Period>, 

  // Timeline Data
  timeline: ComputedRef<TimelineItemCollection>
  timelineLoading: ComputedRef<boolean>

  // Quickstat Data
  quickstats: ComputedRef<ClickStat>
  quickstatsLoading: ComputedRef<boolean>

  // Popular Times data
  populartimes: ComputedRef<HourlyPopularityStats>
  populartimesLoading: ComputedRef<boolean>

  // Popular Days Data
  populardays: ComputedRef<DailyPopularityStats>
  populardaysLoading: ComputedRef<boolean>

  // Top Referrer Data
  topreferrers: ComputedRef<TopReferrerStats>
  topreferrersLoading: ComputedRef<boolean>

  // Top Language Data
  toplanguages: ComputedRef<TopLanguageStats>
  toplanguagesLoading: ComputedRef<boolean>

  // World Traffic Data
  worldtraffic: ComputedRef<WorldTrafficStats>
  worldtrafficLoading: ComputedRef<boolean>

  // General Stats Data
  browserstats: ComputedRef<GeneralStats>
  osstats: ComputedRef<GeneralStats>
  devicestats: ComputedRef<GeneralStats>
  generalstatsLoading: ComputedRef<boolean>
}

/**
 * This class is responsible for handling loading the data for the charts and graphs that display click data. There is a lot going on here because there is a lot of different places that
 * we load data from, but essentially this is just an object that loads data from remote urls and then once loaded. passes that data to preloaded reactive data sources
 */
export default function AnalyticsViewModel({
  org = ref<Organization | null>(null),
  team = ref<Team | null>(null),
  alias = ref<string | null>(null),
  tag = ref<string | null>(null),
  isDebugMode = false,
  debugDataType,
}: AnalyticsViewModelOptions): iAnalyticsViewModel {
  const error = ref<string | null>(null);
  const currentPeriod = ref<Period | null>(null);

  if (!isRef(org)) {
    console.error('team url view model org is not a ref')
  }

  if (!isRef(team)) {
    console.error('team url view model team is not a ref')
  }

  /**
   * The api classes that we use to load data from the server. if debug = true then we load
   * fake data instead of fetching from the server
   */
  const apis = {
    timeline: StatsTimelineAPI(isDebugMode, debugDataType),
    quickStats: QuickStatsAPI(isDebugMode),
    popularTimes: HourlyPopularityStatsAPI(isDebugMode),
    popularDays: DailyPopularityStatsAPI(isDebugMode),
    topReferrer: TopReferrerStatsAPI(isDebugMode),
    topLanguages: TopLanguageStatsAPI(isDebugMode),
    worldTraffic: WorldTrafficAPI(isDebugMode),
    generalStats: GeneralStatsAPI(isDebugMode),
  }

  const thisClass: iAnalyticsViewModel = {
    /*
     * Replace the http router with another implementation
     */
    setRouter: (
      key: keyof typeof apis,
      router: Pick<HttpInterface, 'get'>
    ): iAnalyticsViewModel => {
      apis?.[key]?.setRouter(router)
      return thisClass
    },

    fetch: async function (period = TimePeriods['last_24_hour'], archived = false): Promise<void> {
      const orgID = org.value?.slug
      const teamID = team.value?.slug || null

      error.value = null;

      if (!orgID) {
        error.value = "no organization provided. cannot fetch team urls";

        logError(error.value);

        return;
      }

      const from = period.start().toISOString(true)
      const to = period.end().toISOString(true)

      /**
      * Iterate through all of the apis in this viewmodel loading the data for each one by one. The actual method that we call depends on whether we are viewing
      * an alias, tag, or full team dashboard
      */
      currentPeriod.value = period;

      for (const api of Object.values(apis)) {
        try {
          if (!isNullish(alias.value) && alias.value !== '') {
            await api.fetchForAlias({ orgID, teamID, alias: alias.value as string, from, to, archived })
          } else if (!isNullish(tag.value) && tag.value !== '') {
            await api.fetchForTag({ orgID, teamID, tag: tag.value as string, from, to, archived })
          } else {
            await api.fetchForTeam({ orgID, teamID, from, to, archived })
          }
        } catch {
          logError(`Failed to load ${api.constructor?.name || 'analytics'} data`)
        }
      }
    },

    /**
     * Re-fetches the world traffic dataset against a different map so the location chart
     * can drill into a country and return to the world view. The world traffic API replays
     * the alias/tag/team context captured during the most recent full fetch().
     */
    fetchWorldTrafficMap: async function (map: ValidWorldMapTypes): Promise<void> {
      try {
        await apis.worldTraffic.fetchForMap(map)
      } catch {
        logError(`Failed to load world traffic data for ${map}`)
      }
    },

    download: async function (options: { period?: Period; memberEmail?: string | null; archived?: boolean } = {}): Promise<void> {
      const { period = TimePeriods['last_24_hour'], memberEmail = null, archived = false } = options
      const orgID = org.value?.slug
      const teamID = team.value?.slug || null

      if (!orgID) {
        return
      }

      const from = period.start().toISOString(true)
      const to = period.end().toISOString(true)

      // Build base URL (routes are under /app prefix)
      let baseUrl = `/app/organization/${orgID}`
      if (teamID) {
        baseUrl += `/team/${teamID}`
      }
      baseUrl += `/analytics/download`

      // Build query params
      const params: Record<string, string | string[]> = { from, to, archived: String(archived) }
      if (alias.value) {
        params['alias[]'] = alias.value
      }
      if (tag.value) {
        params['tag[]'] = tag.value
      }
      if (memberEmail) {
        params.member = memberEmail
      }

      // Fetch the file and trigger download
      const response = await axios.get(baseUrl, {
        params,
        responseType: 'blob',
        validateStatus: () => true,
      })

      if (response.status < 200 || response.status >= 300) {
        logError(`Failed to download analytics: ${response.status}`)
        return
      }

      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/)
      const filename = filenameMatch?.[1] || 'analytics.csv'

      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    loading: computed(() => apis.timeline.loading.value
      || apis.quickStats.loading.value
      || apis.popularTimes.loading.value
      || apis.popularDays.loading.value
      || apis.topReferrer.loading.value
      || apis.topLanguages.loading.value
      || apis.worldTraffic.loading.value
      || apis.generalStats.loading.value
    ),

    error: computed(() => error.value),

    currentPeriod: computed(() => currentPeriod.value || TimePeriods["last_24_hour"]),   

    // Timeline Data
    timeline: computed(() => apis.timeline.data.value),
    timelineLoading: computed(() => apis.timeline.loading.value),

    // Quickstat Data
    quickstats: computed(() => apis.quickStats.data.value),
    quickstatsLoading: computed(() => apis.quickStats.loading.value),

    // Popular Times data
    populartimes: computed(() => apis.popularTimes.dataset.value),
    populartimesLoading: computed(() => apis.popularTimes.loading.value),

    // Popular Days Data
    populardays: computed(() => apis.popularDays.dataset.value),
    populardaysLoading: apis.popularDays.loading,

    // Top Referrer Data
    topreferrers: computed(() => apis.topReferrer.dataset.value),
    topreferrersLoading: computed(() => apis.topReferrer.loading.value),

    // Top Language Data
    toplanguages: computed(() => apis.topLanguages.dataset.value),
    toplanguagesLoading: computed(() => apis.topLanguages.loading.value),

    // World Traffic Data
    worldtraffic: computed(() => apis.worldTraffic.dataset.value),
    worldtrafficLoading: computed(() => apis.worldTraffic.loading.value),

    // General Stats Data
    browserstats: computed(() => apis.generalStats.dataset.value.browser),
    osstats: computed(() => apis.generalStats.dataset.value.os),
    devicestats: computed(() => apis.generalStats.dataset.value.device),
    generalstatsLoading: computed(() => apis.generalStats.loading.value),
  }

  return thisClass
}
